/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

export type MergeDeepOptions = {
  serverPrecedenceFields?: string[];
  propertiesToReplace?: {
    path: string;
    allowMultiple: boolean;
  }[];
  propertiesToReArrange?: string[];
};
type PrimaryNavPage = {
  pyLabel: string;
  classID: string;
  pxPageViewIcon: string;
  pyRuleName: string;
  pyClassName: string;
};
type SLA = {
  deadline: string;
  goal: string;
};
type Stage = {
  entryTime: string;
  ID: string;
  links?: {
    open: {
      href: string;
      rel: string;
      title: string;
      type: string;
    };
  };
  name: string;
  type: string;
  visited_status: string;
  transitionType?: string;
};

type Content = {
  classID: string;
  BusinessID?: string;
  Name: string;
  pxObjClass: string;
};
type ActionLink = {
  href: string;
  rel: string;
  title: string;
  type: string;
};
type Action = {
  ID: string;
  name: string;
  type: string;
  links?: {
    [key: string]: ActionLink;
  };
};

type Assignment = {
  ID: string;
  actions?: Action[];
  links?: {
    [key: string]: ActionLink;
  };
  name: string;
  canPerform: boolean;
  assigneeInfo?: object;
  sla?: SLA;
  instructions?: string;
  processID?: string;
  processName?: string;
};
type ActionButton = {
  jsAction: string;
  name: string;
  actionID: string;
  outcomeID?: string;
};
type CaseInfo = {
  caseTypeID: string;
  caseTypeName: string;
  createTime: string;
  createdBy: string;
  ID: string;
  businessID: string;
  lastUpdateTime: string;
  lastUpdatedBy: string;
  name: string;
  owner: string;
  sla: SLA;
  stageID: string;
  stageLabel: string;
  stages: Stage[];
  status: string;
  urgency: string;
  content: Content;
  associations?: {
    follows: boolean;
  };
  headers?: {
    etag: string;
  };
  assignments: Assignment[];
  availableActions: Action[];
  actionButtons: {
    main: ActionButton[];
    secondary: ActionButton[];
  };
  key: string;
};
type Context = {
  caseInfo: CaseInfo;
};
type App = {
  pyPortal: {
    classID: string;
    pyAppViewPageViewSection: string;
    pyLabel: string;
    Template: string;
  };
  pyPrimaryNavPages: PrimaryNavPage[];
};
type Data = {
  [key: string]: Context & App & object;
};
type Item = {
  view: {
    config: object;
    type: string;
  };
  context: string;
};
type Container = {
  [key: string]: {
    type: string;
    accessedOrder: string[];
    items: Item[];
  };
};
export type State = {
  data: Data;
  containers: {
    [key: string]: Container;
  };
};

export type ErrorAction = {
  type: string;
  payload: {
    msg: string;
    err: string;
    context: string;
    formFields: string;
    actionMgrID: string;
    canUpdateAvailableActions: boolean;
  };
};

export type Headers = {
  etag?: string;
  RemoteSystemID?: string;
  'x-origin-channel'?: string;
  'Content-Type'?: string;
  [key: string]: string | undefined;
};

export type CalculatedField = {
  name: string;
  context: string;
  type?: string;
};

export interface ActionWithPayload<T> extends Action {
  payload: T;
}

export type RestAPIPayload = {
  restApi: string;
  reqType: string;
  body?: any;
};

// Define a type for primitive values
export type Primitive = string | number | boolean | null | undefined;

// Define a type for an array of primitives
export type PrimitiveArray = Primitive[];

// Define a type for non-nullable primitive values
export type Basic = Omit<Primitive, 'undefined'>;

export type Nested = NestedObject | NestedObjectArray;
// Define a type for a deeply nested object
export type NestedObject = {
  [key: string]: Basic | Basic[] | Nested;
};

// Define a type for an array of non-nullable nested objects
export interface NestedObjectArray extends Array<Basic | NestedObject> {}

export type NextAssignmentInfoObj = {
  context?: string;
  className?: string;
  links?: {
    open?: {
      rel?: string;
      href?: string;
      type?: string;
      title?: string;
    };
  };
  ID?: string;
};

export type Actionout = {
  type: string;
  payload: {
    context: string;
    nextAssignmentInfo?: NextAssignmentInfoObj;
    data?: {
      [key: string]: any;
    };
    isParallelAssignment?: boolean;
  };
};

export type RootView = {
  type: string;
  config: {
    context: string;
    name: string;
    type: string;
  };
};

// this is an ENUM for the rule types used in localization
export const LocaleRuleTypes = {
  DATAPAGE: 'DATAPAGE',
  VIEW: 'VIEW',
  FIELD: 'FIELD',
  CASE: 'CASE',
  PARAGRAPH: 'PARAGRAPH',
  PORTAL: 'PORTAL'
} as const;
